import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { api } from "../api/client";
import { ApiError } from "../api/errors";
import type { SessionOut } from "../api/types";
import type { Capability } from "./capabilities";

/** Who is signed in and what the server says they may do, held in memory only.
 *
 * NOTHING IS WRITTEN TO localStorage OR sessionStorage. The session lives in an
 * HttpOnly cookie the browser manages; copying identity into web storage would
 * create a second, weaker source of truth that survives logout. The capability
 * list is the same kind of thing and gets the same treatment — it is re-read
 * from the server on every restore and every sign-in, and a tab that has been
 * open since before a demotion gets the new answer the moment it asks.
 *
 * CAPABILITIES ARE FOR DRAWING SCREENS. `can()` decides whether a nav item is
 * rendered and whether a button is offered. It decides nothing else: every
 * request is refused or allowed by the backend on its own authority, so a
 * forged capability list buys nothing but a button that answers 403.
 */
export interface AuthState {
  status: "loading" | "authenticated" | "anonymous";
  email: string | null;
  displayName: string | null;
  /** Exactly what the last session answer said. Never edited locally. */
  capabilities: string[];
  /** True while this account is still using a password an owner generated. */
  mustChangePassword: boolean;
  /** This browser has just replaced its own password and been signed out.
   *
   * Held here rather than passed through the address or router state because
   * the screen that knows it is about to be redirected away by `RequireAuth`,
   * and neither of those survives a redirect it does not control. Cleared by
   * the next successful sign-in. */
  passwordJustChanged: boolean;
  /** Does the server say this administrator may do that? UX only. */
  can: (capability: Capability) => boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Replace your own password. Every session ends, including this one. */
  changePassword: (current: string, next: string) => Promise<void>;
  /** Called when any request reports the session is gone. */
  clearSession: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

/** Read the capability list defensively.
 *
 * An older backend, a proxy that trimmed the body, a fixture written before the
 * field existed — in every one of those the honest answer is "this session
 * claims nothing", which hides sections rather than offering ones that would
 * answer 403. The unsafe default would be assuming everything is permitted.
 */
function capabilitiesOf(session: SessionOut): string[] {
  return Array.isArray(session.capabilities) ? session.capabilities : [];
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState["status"]>("loading");
  const [email, setEmail] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [passwordJustChanged, setPasswordJustChanged] = useState(false);

  const applySession = useCallback((session: SessionOut) => {
    setEmail(session.email);
    setDisplayName(session.display_name);
    setCapabilities(capabilitiesOf(session));
    setMustChangePassword(session.must_change_password === true);
    setStatus(session.authenticated ? "authenticated" : "anonymous");
    setPasswordJustChanged(false);
  }, []);

  const clearSession = useCallback(() => {
    setEmail(null);
    setDisplayName(null);
    // Cleared with the identity, deliberately: an anonymous browser that kept
    // the last administrator's capabilities would draw their sidebar for
    // whoever signs in next, for as long as it took the first request to fail.
    setCapabilities([]);
    setMustChangePassword(false);
    setStatus("anonymous");
  }, []);

  // Session restoration: the cookie may already be valid from a previous visit,
  // so the app asks the server rather than assuming it must show Login.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const session = await api.get<SessionOut>("/admin/auth/session");
        if (!cancelled) applySession(session);
      } catch {
        // 401 is the ordinary "not signed in yet" answer, and a network error
        // cannot be distinguished from it usefully here — either way the only
        // safe assumption is anonymous.
        if (!cancelled) clearSession();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applySession, clearSession]);

  const signIn = useCallback(
    async (emailInput: string, password: string) => {
      const session = await api.post<SessionOut>("/admin/auth/login", {
        email: emailInput,
        password,
      });
      // The login response sets both cookies and carries the same capability
      // list `/session` would; applying it avoids a second round trip.
      applySession(session);
    },
    [applySession],
  );

  const signOut = useCallback(async () => {
    try {
      await api.post<SessionOut>("/admin/auth/logout");
    } catch (error) {
      // A session that is already gone is the outcome we wanted anyway.
      if (!(error instanceof ApiError)) throw error;
    } finally {
      clearSession();
    }
  }, [clearSession]);

  /** Replace your own password, and end up signed out.
   *
   * The server revokes every session for this account — including the one
   * making the request — and clears the cookies, so the only honest thing to
   * show afterwards is the login screen. `RequireAuth` performs that redirect
   * on its own the moment this goes anonymous; the flag is what lets the login
   * screen say WHY, since the screen that knows is the one being redirected
   * away.
   */
  const changePassword = useCallback(
    async (current: string, next: string) => {
      await api.post<SessionOut>("/admin/auth/password", {
        current_password: current,
        new_password: next,
      });
      setPasswordJustChanged(true);
      clearSession();
    },
    [clearSession],
  );

  const granted = useMemo(() => new Set(capabilities), [capabilities]);
  const can = useCallback(
    (capability: Capability) => granted.has(capability),
    [granted],
  );

  const value = useMemo<AuthState>(
    () => ({
      status,
      email,
      displayName,
      capabilities,
      mustChangePassword,
      passwordJustChanged,
      can,
      signIn,
      signOut,
      changePassword,
      clearSession,
    }),
    [
      status,
      email,
      displayName,
      capabilities,
      mustChangePassword,
      passwordJustChanged,
      can,
      signIn,
      signOut,
      changePassword,
      clearSession,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}

/** Shorthand for the common case: one capability, for one control. */
export function useCan(capability: Capability): boolean {
  return useAuth().can(capability);
}
